import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard, ListTodo, CalendarDays, Banknote, Folder, FileSpreadsheet, Mail, Briefcase, AlertTriangle,
  ShieldCheck, Clock, Cake, ArrowRight, Loader2, Sparkles, Flag,
} from 'lucide-react';

const STATUS_LABEL = { draft: 'Draft', in_progress: 'In progress', submitted: 'Submitted', approved: 'Approved', closed: 'Closed' };
const fmtDay = (d) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); };

const Stat = ({ icon: Icon, label, value, tone = 'navy', onClick, testid, sub }) => (
  <button type="button" onClick={onClick} className="text-left bg-white border rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className={`p-2 rounded-lg ${tone === 'red' ? 'bg-red-50 text-red-600' : tone === 'amber' ? 'bg-amber-50 text-amber-600' : tone === 'green' ? 'bg-emerald-50 text-emerald-600' : 'bg-[#0A2A43]/5 text-[#0A2A43]'}`}><Icon className="h-4 w-4" /></span>
      <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500" />
    </div>
    <p className="text-2xl font-bold text-[#0A2A43] mt-3 font-mono" data-testid={`${testid}-value`}>{value}</p>
    <p className="text-xs text-gray-600">{label}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </button>
);

const Panel = ({ title, icon: Icon, action, onAction, children, testid }) => (
  <div className="bg-white border rounded-xl" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <h3 className="text-sm font-bold text-[#0A2A43] flex items-center gap-2"><Icon className="h-4 w-4 text-[#D4AF37]" /> {title}</h3>
      {action && <button type="button" onClick={onAction} className="text-xs text-[#0077B6] hover:underline flex items-center gap-1" data-testid={`${testid}-action`}>{action} <ArrowRight className="h-3 w-3" /></button>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/** Agent portal landing tab. */
export default function AgentDashboard({ apiUrl, agent, onNavigate }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!agent?.id) return;
    (async () => {
      try {
        const r = await fetch(`${apiUrl}/api/agents/${agent.id}/dashboard`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setD(await r.json());
      } catch (e) { setErr(e.message); }
    })();
  }, [apiUrl, agent?.id]);

  if (err) return <p className="text-sm text-red-600" data-testid="dashboard-error">Dashboard unavailable: {err}</p>;
  if (!d) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#0A2A43]" /></div>;

  const hour = new Date().getHours();
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = (d.agent.name || '').split(' ')[0];

  return (
    <div className="space-y-5" data-testid="agent-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-bold flex items-center gap-1"><LayoutDashboard className="h-3.5 w-3.5" /> Dashboard</p>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-[#0A2A43]" data-testid="dashboard-greeting">{hello}, {first}.</h2>
          <p className="text-sm text-gray-500">{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
        {!d.agent.has_birthday && (
          <button type="button" onClick={() => onNavigate('profile')} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-pink-50 text-pink-800 border border-pink-200 hover:bg-pink-100" data-testid="dashboard-birthday-nudge">
            <Cake className="h-3.5 w-3.5" /> Add your date of birth so the team can celebrate with you
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="dashboard-stats">
        <Stat icon={ListTodo} label="Open tasks" value={d.tasks.open} sub={d.tasks.overdue ? `${d.tasks.overdue} overdue` : `${d.tasks.high_priority} high priority`} tone={d.tasks.overdue ? 'red' : 'navy'} onClick={() => onNavigate('tasks')} testid="stat-tasks" />
        <Stat icon={CalendarDays} label="Next 14 days" value={d.upcoming.length} sub="events on your calendar" onClick={() => onNavigate('calendar')} testid="stat-upcoming" />
        {d.mortgage && <Stat icon={Banknote} label="Mortgage files" value={d.mortgage.total} sub={d.mortgage.high_aml ? `${d.mortgage.high_aml} high AML risk` : `${d.mortgage.by_status.in_progress || 0} in progress`} tone={d.mortgage.high_aml ? 'red' : 'navy'} onClick={() => onNavigate('mortgage-workflow')} testid="stat-mortgage" />}
        {d.folders && <Stat icon={Folder} label="Client folders" value={d.folders.total} sub={d.folders.for_review ? `${d.folders.for_review} awaiting compliance review` : `${d.folders.approved} approved`} tone={d.folders.deficient.length ? 'amber' : 'navy'} onClick={() => onNavigate('clients')} testid="stat-folders" />}
        {d.insurance && <Stat icon={FileSpreadsheet} label="Insurance clients" value={d.insurance.clients} sub={`${d.insurance.cases} compliance case(s)`} onClick={() => onNavigate('compliance')} testid="stat-insurance" />}
        <Stat icon={Mail} label="Unread memos" value={d.unread_memos} tone={d.unread_memos ? 'amber' : 'navy'} onClick={() => onNavigate('memos')} testid="stat-memos" />
        {d.carriers != null && !d.insurance && <Stat icon={Briefcase} label="Carrier contracts" value={d.carriers} onClick={() => onNavigate('carriers')} testid="stat-carriers" />}
      </div>

      {d.folders?.deficient?.length > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-red-200 bg-red-50 text-sm" data-testid="dashboard-deficient">
          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
          <div><b className="text-red-800">Compliance review needs your attention:</b> {d.folders.deficient.map((f) => f.client_name).join(', ')} — open the folder to see the officer's notes.
            <button type="button" onClick={() => onNavigate('clients')} className="ml-2 underline text-red-700" data-testid="dashboard-deficient-link">Go to My Clients' Folders</button></div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Coming up" icon={CalendarDays} action="Open calendar" onAction={() => onNavigate('calendar')} testid="dashboard-upcoming">
          {d.upcoming.length === 0 ? <p className="text-sm text-gray-400">Nothing scheduled in the next two weeks.</p> : (
            <ul className="space-y-2">
              {d.upcoming.map((e) => (
                <li key={e.id} className="flex items-start gap-2 text-sm" data-testid={`upcoming-${e.id}`}>
                  <span className="mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: e.color || '#0077B6' }} />
                  <div className="min-w-0">
                    <p className="text-gray-800 truncate">{e.title}</p>
                    <p className="text-[11px] text-gray-400">{fmtDay(e.start_date)}{!e.all_day && e.start_time ? ` · ${e.start_time}` : ''}{e.source === 'autopilot' ? (e.kind === 'birthday' ? ' · team birthday' : ' · holiday') : e.source === 'admin' ? ' · company event' : ''}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Tasks due" icon={ListTodo} action="All tasks" onAction={() => onNavigate('tasks')} testid="dashboard-tasks">
          {d.tasks.next.length === 0 ? <p className="text-sm text-gray-400">No tasks due in the next two weeks.</p> : (
            <ul className="space-y-2">
              {d.tasks.next.map((t) => {
                const overdue = t.due_date < d.date;
                return (
                  <li key={t.id} className="flex items-start gap-2 text-sm" data-testid={`task-${t.id}`}>
                    {overdue ? <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" /> : <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className={`truncate ${overdue ? 'text-red-700 font-medium' : 'text-gray-800'}`}>{t.title}</p>
                      <p className="text-[11px] text-gray-400">{overdue ? 'Overdue · ' : ''}{fmtDay(t.due_date)}{t.due_time ? ` · ${t.due_time}` : ''}{t.priority === 'high' ? ' · high priority' : ''}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {d.mortgage ? (
          <Panel title="Recent mortgage files" icon={Banknote} action="Mortgage workflow" onAction={() => onNavigate('mortgage-workflow')} testid="dashboard-mortgage">
            {d.mortgage.recent.length === 0 ? <p className="text-sm text-gray-400">No mortgage files yet.</p> : (
              <ul className="space-y-2">
                {d.mortgage.recent.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`mfile-${f.id}`}>
                    <span className="truncate text-gray-800">{f.client_name || 'Untitled'}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {f.aml === 'High' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">AML HIGH</span>}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{STATUS_LABEL[f.status] || f.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : (
          <Panel title="Quick links" icon={Sparkles} testid="dashboard-links">
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[['carriers', 'My Carriers', Briefcase], ['compliance', 'Insurance App Workflow', FileSpreadsheet], ['regulatory', 'Regulatory Forms', Flag], ['my-documents', 'My Documents', ShieldCheck]].map(([id, label, Icon]) => (
                <button key={id} type="button" onClick={() => onNavigate(id)} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50 text-left" data-testid={`quicklink-${id}`}><Icon className="h-4 w-4 text-[#0A2A43]" /> {label}</button>
              ))}
            </div>
          </Panel>
        )}
      </div>

      {d.folders && d.folders.recent.length > 0 && (
        <Panel title="Client folders — compliance status" icon={ShieldCheck} action="My Clients' Folders" onAction={() => onNavigate('clients')} testid="dashboard-folders">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {d.folders.recent.map((f) => (
              <div key={f.id} className="border rounded-lg p-2.5 text-sm" data-testid={`folder-${f.id}`}>
                <p className="font-medium text-gray-800 truncate">{f.client_name}</p>
                <p className={`text-[11px] mt-0.5 ${f.status === 'for_review' ? 'text-orange-700' : f.status === 'reviewed' ? 'text-emerald-700' : 'text-gray-400'}`}>
                  {f.status === 'for_review' ? 'Awaiting compliance review' : f.status === 'reviewed' ? 'Reviewed' : 'No final package yet'}
                </p>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
